import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { usePlatformSource } from './usePlatformSource';
import { usePageType } from './usePageType';
import { PageType } from './usePageType/usePageType.types';
import {
  GetAdDetailsResponse,
  AdTransitionSource,
} from '@/api/types/ads.types';
import { GET_AD_DETAILS } from '@/api/queries/ads.queries';
import { getPathWithoutHash } from '@/utils/helpers';
import { getAdTransitionSourceCookie } from '@/utils/cookies';
import { ErrorCodesEnum } from '@/api/types/common.types';
import { useQueryParams } from '@/hooks/useQueryParams';
import { QUERY, TURBOAZ_EMBED_BLOCK_UTM } from '@/constants/common';

interface UseAdDetailsOptions {
  skipQuery?: boolean;
  cacheOnly?: boolean;
}

const usePreviousRoute = () => {
  const { asPath } = useRouter();
  const ref = useRef<string | null>(null);
  const previousRef = useRef<string | null>(null);

  useEffect(() => {
    previousRef.current = ref.current;
    ref.current = asPath;
  }, [asPath]);

  return previousRef.current;
};

export function useAdDetails({
  skipQuery = false,
  cacheOnly = false,
}: UseAdDetailsOptions = {}) {
  const {
    query: { slug },
    asPath,
  } = useRouter();
  const source = usePlatformSource();
  const pageType = usePageType();
  const previousAsPath = usePreviousRoute();

  const [utmCampaign, utmSource] = useQueryParams(
    QUERY.utmCampaign,
    QUERY.utmSource
  );

  const cleanPath = getPathWithoutHash(asPath);
  const cleanPreviousPath = getPathWithoutHash(previousAsPath);
  const legacyId = Array.isArray(slug) ? slug[2] : undefined;
  const isAdPage = pageType === PageType.Ad && !!legacyId;

  const adTransitionSource = useMemo(() => {
    if (
      utmCampaign === TURBOAZ_EMBED_BLOCK_UTM.Campaign &&
      utmSource === TURBOAZ_EMBED_BLOCK_UTM.Source
    ) {
      return AdTransitionSource.EmbedBlock;
    }

    if (!legacyId) return undefined;

    return getAdTransitionSourceCookie(legacyId).get();
  }, [legacyId, utmCampaign, utmSource]);

  const clearAdTransitionSourceCookie = () => {
    if (legacyId) {
      getAdTransitionSourceCookie(legacyId).clear();
    }
  };

  const { data, loading, refetch, previousData, error } =
    useQuery<GetAdDetailsResponse>(GET_AD_DETAILS, {
      variables: {
        legacyId,
        source,
        ...(adTransitionSource && {
          transitionSourceBlock: adTransitionSource,
        }),
      },
      errorPolicy: 'all',
      fetchPolicy: cacheOnly ? 'cache-only' : 'cache-first',
      nextFetchPolicy: cacheOnly ? 'cache-only' : 'network-only',
      skip: !isAdPage || skipQuery,
      onCompleted: clearAdTransitionSourceCookie,
      onError: clearAdTransitionSourceCookie,
    });

  const hasAdNotFoundError = !!error?.graphQLErrors?.some(
    ({ extensions }) => extensions?.code === ErrorCodesEnum.NotFoundError
  );

  useEffect(() => {
    if (!isAdPage) {
      clearAdTransitionSourceCookie();
    }

    if (cacheOnly || previousAsPath === null) {
      return;
    }

    if (cleanPreviousPath !== cleanPath && isAdPage) {
      refetch();
    }
  }, [cacheOnly, cleanPath, cleanPreviousPath, isAdPage, refetch]);

  return {
    adDetails: data?.adDetails,
    loading,
    refetch,
    previousData,
    hasAdNotFoundError,
  };
}
